import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import FormShell from "@/components/FormShell";
import PersonalInfoStep from "@/components/PersonalInfoStep";
import FileUploader from "@/components/FileUploader";
import CustomFieldsRenderer from "@/components/CustomFieldsRenderer";
import { useSiteConfig } from "@/lib/siteConfig";
import { api } from "@/lib/api";

const STEPS = ["Personal information", "Health profile", "Coverage & documents"];

export default function HealthForm() {
  const navigate = useNavigate();
  const { config } = useSiteConfig();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [personal, setPersonal] = useState({});
  const [health, setHealth] = useState({ smoker: false });
  const [files, setFiles] = useState([]);
  const [custom, setCustom] = useState({});
  const customFields = config.health_custom_fields || [];

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const validatePersonal = () => {
    const required = ["full_name", "email", "phone", "dob", "gender", "address", "city", "state", "postal_code", "id_number"];
    for (const f of required) {
      if (!personal[f]) {
        toast.error(`Please fill in ${f.replace("_", " ")}`);
        return false;
      }
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        insurance_type: "health",
        ...personal,
        health_details: {
          ...health,
          height_cm: health.height_cm ? Number(health.height_cm) : null,
          weight_kg: health.weight_kg ? Number(health.weight_kg) : null,
          coverage_amount: health.coverage_amount ? Number(health.coverage_amount) : null,
        },
        file_ids: files.map((f) => f.id),
        custom_fields: custom,
      };
      const { data } = await api.post("/clients", payload);
      toast.success("Application submitted successfully!");
      navigate("/apply/success", { state: { tracker_url: data.tracker_url, tracker_token: data.tracker_token } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormShell
      title={
        <span className="inline-flex items-center gap-3">
          <Heart className="w-7 h-7 text-clay" strokeWidth={1.5} /> Health Insurance Application
        </span>
      }
      subtitle={STEPS[step]}
      step={step}
      totalSteps={STEPS.length}
    >
      {step === 0 && <PersonalInfoStep data={personal} set={setPersonal} />}

      {step === 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-forest">Height (cm)</Label>
            <Input
              type="number"
              value={health.height_cm || ""}
              onChange={(e) => setHealth({ ...health, height_cm: e.target.value })}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="input-height"
            />
          </div>
          <div>
            <Label className="text-forest">Weight (kg)</Label>
            <Input
              type="number"
              value={health.weight_kg || ""}
              onChange={(e) => setHealth({ ...health, weight_kg: e.target.value })}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="input-weight"
            />
          </div>
          <div>
            <Label className="text-forest">Blood group</Label>
            <Select value={health.blood_group || ""} onValueChange={(v) => setHealth({ ...health, blood_group: v })}>
              <SelectTrigger className="mt-1.5 h-11 border-line" data-testid="select-blood-group">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {["A+","A-","B+","B-","O+","O-","AB+","AB-","Unknown"].map((g) => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-forest">Exercise frequency</Label>
            <Select value={health.exercise_frequency || ""} onValueChange={(v) => setHealth({ ...health, exercise_frequency: v })}>
              <SelectTrigger className="mt-1.5 h-11 border-line" data-testid="select-exercise">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Never</SelectItem>
                <SelectItem value="rarely">Rarely</SelectItem>
                <SelectItem value="weekly">1-2x weekly</SelectItem>
                <SelectItem value="active">3+ times weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-forest">Pre-existing conditions</Label>
            <Textarea
              value={health.pre_existing_conditions || ""}
              onChange={(e) => setHealth({ ...health, pre_existing_conditions: e.target.value })}
              rows={3}
              placeholder="List any chronic conditions, allergies, etc. Type 'None' if not applicable."
              className="mt-1.5 border-line input-clean resize-none"
              data-testid="input-conditions"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-forest">Current medications</Label>
            <Textarea
              value={health.current_medications || ""}
              onChange={(e) => setHealth({ ...health, current_medications: e.target.value })}
              rows={2}
              className="mt-1.5 border-line input-clean resize-none"
              data-testid="input-medications"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-forest">Past surgeries</Label>
            <Textarea
              value={health.surgeries || ""}
              onChange={(e) => setHealth({ ...health, surgeries: e.target.value })}
              rows={2}
              className="mt-1.5 border-line input-clean resize-none"
              data-testid="input-surgeries"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-forest">Family medical history</Label>
            <Textarea
              value={health.family_medical_history || ""}
              onChange={(e) => setHealth({ ...health, family_medical_history: e.target.value })}
              rows={2}
              className="mt-1.5 border-line input-clean resize-none"
              data-testid="input-family-history"
            />
          </div>
          <div>
            <Label className="text-forest">Alcohol consumption</Label>
            <Select value={health.alcohol_consumption || ""} onValueChange={(v) => setHealth({ ...health, alcohol_consumption: v })}>
              <SelectTrigger className="mt-1.5 h-11 border-line" data-testid="select-alcohol">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Never</SelectItem>
                <SelectItem value="occasional">Occasional</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="frequent">Frequent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between bg-cream border border-line rounded-sm px-4 py-3 mt-7">
            <Label className="text-forest">Smoker</Label>
            <Switch
              checked={!!health.smoker}
              onCheckedChange={(v) => setHealth({ ...health, smoker: v })}
              data-testid="switch-smoker"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div>
            <Label className="text-forest">Desired coverage amount (USD)</Label>
            <Input
              type="number"
              value={health.coverage_amount || ""}
              onChange={(e) => setHealth({ ...health, coverage_amount: e.target.value })}
              placeholder="e.g. 250000"
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="input-coverage"
            />
          </div>
          <FileUploader
            files={files}
            setFiles={setFiles}
            label="Supporting documents (ID proof, medical records, prescriptions)"
            testId="health-uploader"
          />
          {customFields.length > 0 && (
            <div className="pt-6 border-t border-line">
              <h3 className="font-heading text-lg mb-1">Additional information</h3>
              <p className="text-sm text-sage mb-4">Help your agent place you with the right plan.</p>
              <CustomFieldsRenderer
                fields={customFields}
                values={custom}
                onChange={(id, v) => setCustom((c) => ({ ...c, [id]: v }))}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between mt-10 pt-6 border-t border-line">
        <Button
          variant="outline"
          onClick={prev}
          disabled={step === 0}
          className="border-line text-sage hover:text-forest hover:border-forest rounded-sm h-11 px-5"
          data-testid="form-prev-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            onClick={() => {
              if (step === 0 && !validatePersonal()) return;
              next();
            }}
            className="bg-forest hover:bg-forestdark text-bone rounded-sm h-11 px-6"
            data-testid="form-next-btn"
          >
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-clay hover:bg-claydark text-bone rounded-sm h-11 px-6"
            data-testid="form-submit-btn"
          >
            {submitting ? "Submitting…" : "Submit application"} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </FormShell>
  );
}
